import React, { memo, useState } from 'react'
import { Text, AdvancedTooltip } from 'common-components'
import * as Styled from './styled'
import { IAIVideoModel } from 'types/aiVideo'
import CrownIcon from 'assets/svgr-icons/Crown.svg'
import VolumeIcon from 'assets/svgr-icons/Volume.svg'
import { isPremiumModel, getDeviceVersion } from 'utils'
import { translate } from 'utils/translations'
import { transformTextVariables } from 'utils/text-transformation'
import { T } from 'consts/translationKeys'
import {
  AI_VIDEO_MODEL_CODES,
  GENERAL_UPGRADE_POPUP_TYPE,
  UPGRADE_POPUP_OPENER_REF
} from 'consts'
import { useSelector } from 'react-redux'
import { userSelectors } from 'store/features/user/selectors'

const VideoModelItem: React.FC<{
  model: IAIVideoModel
  isSelected: boolean
  onSelect: () => void
  isPremium: boolean
}> = memo(({ model, isSelected, onSelect, isPremium }) => {
  const [modelItemRef, setModelItemRef] = useState<HTMLDivElement | null>(null)
  const [volumeIconRef, setVolumeIconRef] = useState<HTMLDivElement | null>(
    null
  )

  const isMobile = getDeviceVersion()
  const { name, description, capabilities } = model

  return (
    <Styled.ModelItemWrapper
      $isSelected={isSelected}
      onClick={onSelect}
      ref={setModelItemRef}
    >
      <Styled.ModelTextContent>
        <Styled.ModelNameWrapper>
          <Text
            type="subtitle"
            color={isSelected ? 'secondaryDark' : 'secondarySemiDark'}
          >
            {name}
          </Text>
          {isPremium && (
            <Styled.CrownBadgeIcon
              icon={<CrownIcon />}
              size="small"
              color="goldDark"
            />
          )}
          {capabilities.isAudioGenerationSupported && (
            <Styled.ModelVolumeIconWrapper>
              <Styled.VolumeIconBox ref={setVolumeIconRef}>
                <VolumeIcon />
              </Styled.VolumeIconBox>
              {!isMobile && (
                <AdvancedTooltip
                  btnRef={{ current: volumeIconRef }}
                  tooltipText={translate(T.SUPPORTS_AUDIO_GENERATION)}
                  tooltipPosition="top"
                  maxWidth={115}
                  textAlign="center"
                />
              )}
            </Styled.ModelVolumeIconWrapper>
          )}
        </Styled.ModelNameWrapper>
        {description &&
          (isMobile ? (
            <Text type="h4" color="secondary" lineHeight="1.3">
              {description}
            </Text>
          ) : (
            <AdvancedTooltip
              btnRef={{ current: modelItemRef }}
              tooltipText={description}
              tooltipPosition="right"
              maxWidth={300}
              textAlign="left"
            />
          ))}
      </Styled.ModelTextContent>
    </Styled.ModelItemWrapper>
  )
})

VideoModelItem.displayName = 'VideoModelItem'

const ModelDropdownContent: React.FC<{
  models: Array<IAIVideoModel>
  selectedModelCode: string | null
  onSelect: (modelCode: string) => void
  userCurrentPlanId: number
}> = ({ models, selectedModelCode, onSelect, userCurrentPlanId }) => {
  const unlimitedAiVideoModels = useSelector(
    userSelectors.unlimitedAiVideoModels
  )

  const handleBannerClick = () => {
    window.openUpgradePopup({
      type: GENERAL_UPGRADE_POPUP_TYPE,
      openerRef: UPGRADE_POPUP_OPENER_REF,
      targetPlanIds: [28, 29]
    })
  }

  const isModelUnlimited = (modelId: number) =>
    unlimitedAiVideoModels.some(m => m.aiVideoModelId === modelId)

  return (
    <Styled.DropdownListWrapper>
      {models.map((model: IAIVideoModel) => {
        const isRenderforest = model.model === AI_VIDEO_MODEL_CODES.RENDERFOREST
        const showBanner = isRenderforest && !isModelUnlimited(model.id)

        return (
          <React.Fragment key={model.id}>
            {showBanner ? (
              <Styled.RenderforestGroupWrapper>
                <VideoModelItem
                  model={model}
                  isSelected={model.model === selectedModelCode}
                  onSelect={() => onSelect(model.model)}
                  isPremium={isPremiumModel(model, userCurrentPlanId)}
                />
                <Styled.UnlimitedBannerWrapper onClick={handleBannerClick}>
                  <Styled.UnlimitedBannerText>
                    {transformTextVariables(
                      translate(T.UPGRADE_UNLIMITED_GENERATIONS_BANNER),
                      [
                        {
                          value: 'unlimited',
                          Comp: Styled.UnlimitedHighlight
                        }
                      ]
                    )}
                  </Styled.UnlimitedBannerText>
                </Styled.UnlimitedBannerWrapper>
              </Styled.RenderforestGroupWrapper>
            ) : (
              <VideoModelItem
                model={model}
                isSelected={model.model === selectedModelCode}
                onSelect={() => onSelect(model.model)}
                isPremium={isPremiumModel(model, userCurrentPlanId)}
              />
            )}
          </React.Fragment>
        )
      })}
    </Styled.DropdownListWrapper>
  )
}

export default memo(ModelDropdownContent)
